import { useEffect, useState } from 'react'

import {
  Button,
  Text,
  Link,
  Card,
  Image,
  CardColors,
  Badge,
  Separator,
  Loader,
} from '@/ui'
import {
  FinancialAdvisoryProfile,
  getUpriseCalendlyEventDetails,
} from '../financialAdvisory.slice'
import { useAnalyticsView } from '@/features/amplitude'
import { fetchUserFinancialAdvisoryCallEventsIfNeeded } from '../userFinancialAdvisoryCallEvents.slice'
import { useReselector } from '@/utils/sharedHooks'
import { getIntroUserFinancialAdvisoryCallEvents } from '../financialAdvisory.selectors'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { UpriseIntroCard } from './IntroCallCTAPanel'

import { TextColors, TypographyStyles } from '@/styles'

export const AdvisorHelpSummaryCard = () => {
  return (
    <Card color={CardColors.EMPHASIS}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Text variant={TypographyStyles.H1}>
          Get to the financial future you deserve
        </Text>
        <Text variant={TypographyStyles.H2} className="mt-4">
          With personalized financial advising and planning that&#39;s proven to
          work for practices like yours.
        </Text>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
          <div className="flex justify-start mb-5">
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/advisor_piggy_bank.svg"
              alt="heard financial advisory"
              className="w-[100px]"
            />
          </div>
          <Text variant={TypographyStyles.H3}>Expert Driven</Text>
          <Text variant={TypographyStyles.BODY_MD} className="mt-2">
            Dedicated CFP® advisor who understands therapy practices and can
            help with:
          </Text>
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            <li>Business structure decisions</li>
            <li>Retirement planning</li>
            <li>Student loan repayment strategies</li>
            <li>Saving strategies</li>
            <li>And more</li>
          </ul>

          {/* Step by Step Guidance */}
          <div>
            <div className="flex justify-start mb-5">
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/step_guidance.png"
                alt="heard financial advisory"
                width="100"
              />
            </div>
            <Text variant={TypographyStyles.H3}>Step by Step Guidance</Text>
            <Text variant={TypographyStyles.BODY_MD} className="mt-2">
              Real time, step by step guidance:
            </Text>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>Financial goals broken down into actionable steps</li>
              <li>Prioritized recommendations</li>
              <li>Personalized for your financial priorities</li>
            </ul>
          </div>

          {/* Continuous, Ongoing Support */}
          <div>
            <div className="flex justify-start mb-5">
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/advisor_financials.svg"
                alt="heard financial advisory"
                width="100"
              />
            </div>
            <Text variant={TypographyStyles.H3}>
              Continuous, Ongoing Support
            </Text>
            <Text variant={TypographyStyles.BODY_MD} className="mt-2">
              Help when you need it, how you need it:
            </Text>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>Unlimited messaging</li>
              <li>Unlimited plan revisions when goals change</li>
              <li>Goal tracking</li>
              <li>1:1 video calls (add on)</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  )
}

/**
 * This Panel shows after a User has scheduled their intro Advisory Call
 */
export const PreIntroCallPanel = ({
  faProfile,
}: {
  faProfile: FinancialAdvisoryProfile
}) => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const [fetching, setFetching] = useState(false)
  const [calendlyJoinUrl, setCalendlyJoinUrl] = useState('')
  const introCallEvent = useReselector(getIntroUserFinancialAdvisoryCallEvents)
  const advisorName =
    introCallEvent?.rawCalendlyEventBlob?.event_memberships[0]?.user_name

  useEffect(() => {
    async function fetchCalendlyEvent() {
      if (introCallEvent?.rawCalendlyEventBlob?.uri) {
        const calendlyResponse = await getUpriseCalendlyEventDetails(
          introCallEvent?.rawCalendlyEventBlob?.uri
        )(dispatch)
        if (calendlyResponse) {
          setCalendlyJoinUrl(calendlyResponse?.location?.join_url)
        }
      }
    }
    fetchCalendlyEvent()
  }, [dispatch, introCallEvent])

  useEffect(() => {
    async function fetchData() {
      setFetching(true)
      await dispatch(fetchUserFinancialAdvisoryCallEventsIfNeeded())
      setFetching(false)
    }
    fetchData()
  }, [dispatch])

  useEffect(() => {
    pageView('advisory: pre intro call panel')
  }, [pageView])

  if (!fetching && faProfile) {
    return (
      <>
        <div className="flex flex-col lg:flex-row rounded overflow-hidden pt-12">
          {/* Left Column */}
          <div className="w-full lg:w-2/5 bg-natural p-5">
            <div className="max-w-md mx-auto text-center space-y-6">
              <Text variant={TypographyStyles.H1} className="text-forest">
                Here&apos;s what to expect next
              </Text>
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/Clock.svg"
                height="300"
                alt="heard financial advisory"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-3/5 bg-stone p-5">
            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title:
                    'Attend your free 20 Minute Intro Call with your financial advisor',
                  desc: 'Learn how you can benefit from Advisory, and get your questions around the experience answered.',
                },
                {
                  step: 2,
                  title: 'Decide if advisory services will be a fit',
                  desc: 'After your intro call, you’ll have a good understanding around the experience, and if it is a fit for your goals.',
                },
                {
                  step: 3,
                  title:
                    'Add the Advisory Add On to onboard and receive your 100% personalized and comprehensive financial plan.',
                  desc: 'If you decide that Advisory is a fit, onboard to share your full financial picture with your Advisor to receive a personalized, comprehensive financial plan.',
                },
                {
                  step: 4,
                  title: 'Work towards your financial goals, together',
                  desc: 'Review and access your financial plan and recommended actions, with the ability to message your advisor at any point in time. Unlimited revisions and unlimited messaging, with 1:1 calls if needed.',
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex items-start gap-4">
                  <Badge color="olive" size="md">
                    <Text
                      variant={TypographyStyles.BODY_LG}
                      className="text-white"
                    >
                      {step}
                    </Text>
                  </Badge>
                  <div>
                    <Text
                      variant={TypographyStyles.BODY_MD}
                      className="font-bold"
                    >
                      {title}
                    </Text>
                    <Text variant={TypographyStyles.BODY_MD}>{desc}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="my-10">
          <Text variant={TypographyStyles.H2} className="pl-2">
            Your upcoming call
          </Text>

          {introCallEvent && (
            <Card color={CardColors.LEVEL_0}>
              <div className="flex flex-col lg:flex-row items-center gap-6 px-4 py-6">
                <div className="w-full lg:w-1/4 flex justify-center">
                  <Image
                    src="https://heard-images.s3.amazonaws.com/assets/calendar_month.svg"
                    height={100}
                    alt="heard financial advisory"
                  />
                </div>
                <div className="w-full lg:w-1/2 space-y-2">
                  <Text variant={TypographyStyles.EYEBROW}>Date</Text>
                  <Text variant={TypographyStyles.H2}>
                    {DateTime.fromISO(introCallEvent.startTime).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_SHORT
                    )}
                  </Text>
                  <Text variant={TypographyStyles.H2}>
                    {DateTime.fromISO(introCallEvent.startTime).toFormat(
                      DATE_FORMATS_LUXON.TIME
                    )}{' '}
                    -{' '}
                    {DateTime.fromISO(introCallEvent.endTime).toFormat(
                      DATE_FORMATS_LUXON.TIME
                    )}
                  </Text>
                  <Text variant={TypographyStyles.EYEBROW}>
                    Certified Financial Planner
                  </Text>
                  <Text variant={TypographyStyles.H2}>{advisorName}</Text>
                  <Text variant={TypographyStyles.BODY_MD}>
                    Please join the Video Call by clicking &#34;Join Call&#34;
                    on the button on the right, or in your calendar invite.
                  </Text>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    * Please provide 24 hour notice to cancel or reschedule, or
                    you may be charged a fee.
                  </Text>
                </div>
                <div className="w-full lg:w-1/4">
                  <Link newPage href={calendlyJoinUrl}>
                    <Button variant="primary" className="w-full">
                      Join Call
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          )}
        </div>

        <Separator />

        <Text variant={TypographyStyles.H2}>
          An Overview of the Advisory Experience
        </Text>
        <div className="my-6">
          <AdvisorHelpSummaryCard />
        </div>

        <div className="my-6">
          <UpriseIntroCard />
        </div>
      </>
    )
  } else {
    return <Loader active={fetching} />
  }
}
